import { Alert, Button, Stack, Table, Text } from '@mantine/core'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { EventCandidateRunStatus } from 'src/apis/eventCandidateAPI'
import { formatDateTimeMinus } from 'src/variable/constant'
import { RunState, summarizeRun } from './eventCandidateRun'

interface EventCandidateRunPanelProps {
	status: EventCandidateRunStatus | null
	// A run was started here and the status does not show it yet.
	waiting: boolean
	statusError: string
	// A run ended while this page was following it: open the details.
	finishedHere: boolean
}

const ENDED: Partial<Record<RunState, { color: string; title: string }>> = {
	failed: { color: 'red', title: 'Finding events failed' },
	skipped: { color: 'orange', title: 'The last run was skipped' },
	problems: { color: 'orange', title: 'The last run ended with errors' },
	finished: { color: 'green', title: 'Finished finding events' },
}

const formatLocal = (value: number | null) =>
	value === null ? 'unknown' : dayjs(value).format(formatDateTimeMinus)

const plural = (value: number, word: string) =>
	`${value} ${word}${value === 1 ? '' : 's'}`

function EventCandidateRunPanel({
	status,
	waiting,
	statusError,
	finishedHere,
}: EventCandidateRunPanelProps) {
	const summary = useMemo(() => summarizeRun(status), [status])
	const [expanded, setExpanded] = useState(false)

	useEffect(() => {
		if (finishedHere) setExpanded(true)
	}, [finishedHere])

	const statusProblem = statusError && (
		<Text size="sm" color="red">
			Could not read the run status: {statusError}
		</Text>
	)

	if (waiting || summary.state === 'running') {
		return (
			<Alert color="blue" title="Finding events">
				<Stack spacing={4}>
					<Text size="sm">
						{summary.state === 'running' && summary.startedAt !== null
							? `A run started ${formatLocal(summary.startedAt)} (your local time) and is still working.`
							: 'A run has started.'}{' '}
						This can take a few minutes. The page checks every 10 seconds and
						reloads the list when the run ends. You can keep reviewing.
					</Text>
					{statusProblem}
				</Stack>
			</Alert>
		)
	}

	const ended = ENDED[summary.state]
	if (!ended) return statusProblem || null

	const cityErrors = summary.cities.filter((city) => city.error)
	const hasDetails = summary.dropped.length > 0 || summary.cities.length > 0

	return (
		<Alert color={ended.color} title={ended.title}>
			<Stack spacing={4} align="flex-start">
				<Text size="sm">
					Started {formatLocal(summary.startedAt)}, finished{' '}
					{formatLocal(summary.finishedAt)} (your local time).
				</Text>
				{summary.error && (
					<Text size="sm" color="red" sx={{ wordBreak: 'break-word' }}>
						Error: {summary.error}
					</Text>
				)}
				{summary.skipped && <Text size="sm">Reason: {summary.skipped}</Text>}
				<Text size="sm">
					Found <b>{plural(summary.found, 'event')}</b>, added{' '}
					<b>{summary.created}</b> for review, dropped{' '}
					<b>{summary.droppedTotal}</b>.
				</Text>
				{cityErrors.map((city, index) => (
					<Text
						key={`${city.name}-${index}`}
						size="sm"
						color="red"
						sx={{ wordBreak: 'break-word' }}
					>
						{city.name}: {city.error}
					</Text>
				))}
				{statusProblem}
				{hasDetails && (
					<Button
						compact
						variant="subtle"
						color="gray"
						onClick={() => setExpanded((previous) => !previous)}
					>
						{expanded ? 'Hide details' : 'Show details'}
					</Button>
				)}
				{hasDetails && expanded && (
					<Stack spacing={4} sx={{ width: '100%' }}>
						{summary.dropped.map((line) => (
							<Text key={line.label} size="sm">
								Dropped, {line.label.toLowerCase()}: <b>{line.count}</b>
							</Text>
						))}
						{summary.cities.length > 0 && (
							<Table fontSize="sm" verticalSpacing={4} sx={{ maxWidth: 760 }}>
								<thead>
									<tr>
										<th>City</th>
										<th>Found</th>
										<th>Added</th>
										<th>Dropped</th>
										<th>Error</th>
									</tr>
								</thead>
								<tbody>
									{summary.cities.map((city, index) => (
										<tr key={`${city.name}-${index}`}>
											<td>{city.name}</td>
											<td>{city.found}</td>
											<td>{city.created}</td>
											<td>
												{city.droppedTotal}
												{city.dropped.length > 0 &&
													` (${city.dropped
														.map(
															(line) => `${line.label.toLowerCase()} ${line.count}`,
														)
														.join(', ')})`}
											</td>
											<td style={{ wordBreak: 'break-word' }}>
												{city.error || ''}
											</td>
										</tr>
									))}
								</tbody>
							</Table>
						)}
					</Stack>
				)}
			</Stack>
		</Alert>
	)
}

export default EventCandidateRunPanel
